import React, { useState } from 'react';
import './OnboardingModal.css';

const OnboardingModal = ({ isOpen, onComplete }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    userType: '',
    jobSeekerStatus: '',
    referralOptIn: null,
    chromeExtension: null
  });

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    if (step < 5) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = () => {
    // Store in localStorage
    localStorage.setItem('userOnboarding', JSON.stringify({
      phoneNumber: formData.phoneNumber,
      userType: formData.userType,
      jobSeekerStatus: formData.jobSeekerStatus,
      referralOptIn: formData.referralOptIn,
      chromeExtension: formData.chromeExtension,
      completedAt: new Date().toISOString()
    }));
    
    // Mark onboarding as complete
    localStorage.setItem('onboardingComplete', 'true');
    
    onComplete();
  };

  const canProceed = () => {
    switch(step) {
      case 1: return formData.phoneNumber.length >= 10;
      case 2: return formData.userType !== '';
      case 3: return formData.jobSeekerStatus !== '';
      case 4: return formData.referralOptIn !== null;
      case 5: return formData.chromeExtension !== null;
      default: return false;
    }
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal">
        <div className="onboarding-header">
          <h2>Welcome to JobQue!</h2>
          <p>Help us personalize your experience</p>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(step / 5) * 100}%` }}></div>
          </div>
          <span className="step-indicator">Step {step} of 5</span>
        </div>

        <div className="onboarding-content">
          {step === 1 && (
            <div className="onboarding-step">
              <h3>What's your phone number?</h3>
              <p className="step-description">We'll only use this for important account updates</p>
              <input
                type="tel"
                placeholder="(123) 456-7890"
                value={formData.phoneNumber}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/\D/g, "");
                  handleInputChange("phoneNumber", numericValue);
                }}
                className="onboarding-input"
                maxLength="15"
                inputMode="numeric"
              />
            </div>
          )}

          {step === 2 && (
            <div className="onboarding-step">
              <h3>What best describes you?</h3>
              <div className="options-grid">
                <button
                  className={`option-card ${formData.userType === 'student' ? 'selected' : ''}`}
                  onClick={() => handleInputChange('userType', 'student')}
                >
                  <span className="option-label">Student</span>
                </button>
                <button
                  className={`option-card ${formData.userType === 'career_changer' ? 'selected' : ''}`}
                  onClick={() => handleInputChange('userType', 'career_changer')}
                >
                  <span className="option-label">Career Changer</span>
                </button>
                <button
                  className={`option-card ${formData.userType === 'professional' ? 'selected' : ''}`}
                  onClick={() => handleInputChange('userType', 'professional')}
                >
                  <span className="option-label">Professional</span>
                </button>
                <button
                  className={`option-card ${formData.userType === 'other' ? 'selected' : ''}`}
                  onClick={() => handleInputChange('userType', 'other')}
                >
                  <span className="option-label">Other</span>
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="onboarding-step">
              <h3>How actively are you job searching?</h3>
              <div className="options-vertical">
                <button
                  className={`option-card-large ${formData.jobSeekerStatus === 'casual' ? 'selected' : ''}`}
                  onClick={() => handleInputChange('jobSeekerStatus', 'casual')}
                >
                  <div className="option-text">
                    <span className="option-label">Casual Job Seeker</span>
                    <span className="option-sublabel">Just browsing opportunities</span>
                  </div>
                </button>
                <button
                  className={`option-card-large ${formData.jobSeekerStatus === 'active' ? 'selected' : ''}`}
                  onClick={() => handleInputChange('jobSeekerStatus', 'active')}
                >
                  <div className="option-text">
                    <span className="option-label">Active Job Seeker</span>
                    <span className="option-sublabel">Actively applying to jobs</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="onboarding-step">
              <h3>Join our referral network?</h3>
              <p className="step-description">Earn 10 credits for each successful referral!</p>
              <div className="options-vertical">
                <button
                  className={`option-card-large ${formData.referralOptIn === true ? 'selected' : ''}`}
                  onClick={() => handleInputChange('referralOptIn', true)}
                >
                  <div className="option-text">
                    <span className="option-label">Yes, I'm interested</span>
                    <span className="option-sublabel">Start earning rewards</span>
                  </div>
                </button>
                <button
                  className={`option-card-large ${formData.referralOptIn === false ? 'selected' : ''}`}
                  onClick={() => handleInputChange('referralOptIn', false)}
                >
                  <div className="option-text">
                    <span className="option-label">No, maybe later</span>
                    <span className="option-sublabel">You can join anytime</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="onboarding-step">
              <h3>Get our Chrome Extension</h3>
              <p className="step-description">Track jobs directly from company career pages</p>
              <div className="extension-preview">
                <div className="extension-icon">🧩</div>
                <p className="extension-benefit">Add jobs with one click while browsing</p>
              </div>
              <div className="options-vertical">
                <button
                  className={`option-card-large ${formData.chromeExtension === true ? 'selected' : ''}`}
                  onClick={() => handleInputChange('chromeExtension', true)}
                >
                  <div className="option-text">
                    <span className="option-label">Install Extension</span>
                    <span className="option-sublabel">Opens Chrome Web Store</span>
                  </div>
                </button>
                <button
                  className={`option-card-large ${formData.chromeExtension === false ? 'selected' : ''}`}
                  onClick={() => handleInputChange('chromeExtension', false)}
                >
                  <div className="option-text">
                    <span className="option-label">Skip for now</span>
                    <span className="option-sublabel">You can install it later</span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="onboarding-footer">
          {step > 1 && (
            <button onClick={handleBack} className="btn-back">
              Back
            </button>
          )}
          
          {step < 5 ? (
            <button 
              onClick={handleNext} 
              className="btn-next"
              disabled={!canProceed()}
            >
              Next
            </button>
          ) : (
            <button 
              onClick={handleSubmit} 
              className="btn-complete"
              disabled={!canProceed()}
            >
              Complete Setup
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;